import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useCustomEventListener } from 'react-custom-events'
import { Modal, ButtonDrawer } from '~/components'

/**
 * Global React modal — replaces the legacy APP.trigger(APP.events.SHOW_MODAL) system.
 * Mount once inside StoreContext. Triggered via showModal() / showInfoModal() / showContentModal() from ~/lib/modal.
 *
 * Supports three variants from Figma 209-5177:
 *   type='modal'       — centered title + centered text + flush buttons (default for confirmations)
 *   type='information' — info icon + left title + left text + flush buttons
 *   content render fn  — arbitrary JSX with a close button (via SHOW_GLOBAL_CONTENT_MODAL)
 *
 * Button order convention (matching legacy): first button = confirm, second = cancel.
 * Display order (matching Figma): cancel on left, confirm on right.
 * So the array is reversed before passing to ButtonDrawer (where last = primary/right).
 */

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
    <g clipPath="url(#gm-info-clip)">
      <path fillRule="evenodd" clipRule="evenodd" d="M8 14.4333C11.553 14.4333 14.4333 11.553 14.4333 8C14.4333 4.44697 11.553 1.56667 8 1.56667C4.44697 1.56667 1.56667 4.44697 1.56667 8C1.56667 11.553 4.44697 14.4333 8 14.4333ZM8 15.5C12.1421 15.5 15.5 12.1421 15.5 8C15.5 3.85786 12.1421 0.5 8 0.5C3.85786 0.5 0.5 3.85786 0.5 8C0.5 12.1421 3.85786 15.5 8 15.5Z" fill="#17181A"/>
      <rect x="7.2" y="4" width="1.6" height="1.6" rx="0.8" fill="#17181A"/>
      <rect x="7.467" y="6.667" width="1.067" height="5.333" rx="0.213" fill="#17181A"/>
    </g>
    <defs>
      <clipPath id="gm-info-clip"><rect width="16" height="16" fill="white"/></clipPath>
    </defs>
  </svg>
)

export default function GlobalModal() {
  const [state, setState] = useState(null)

  useCustomEventListener('SHOW_GLOBAL_MODAL', ({ message, buttons, isWide, title, type }) => {
    setState({ message, buttons: buttons || [], isWide, title, type: type || 'modal' })
  })

  // Content variant: caller passes a render function (close) => JSX
  useCustomEventListener('SHOW_GLOBAL_CONTENT_MODAL', (render) => {
    setState({ render })
  })

  useCustomEventListener('CLOSE_GLOBAL_MODAL', () => setState(null))

  if (!state) return null

  const close = () => setState(null)

  if (state.render) {
    return createPortal(
      <Modal isOpen rounded onClose={close}>
        {state.render(close)}
      </Modal>,
      document.body
    )
  }

  // Map legacy {title, callback} → ButtonDrawer {title, func}
  // Reverse so confirm (index 0) becomes last (= primary/right in ButtonDrawer)
  const mapped = state.buttons.map(btn => ({
    title: btn.title,
    func: () => { btn.callback?.(); close() },
  }))
  const displayButtons = mapped.length > 1 ? [...mapped].reverse() : mapped
  if (displayButtons.length === 0) displayButtons.push({ title: 'OK', func: close })

  const isInfo = state.type === 'information'

  return createPortal(
    <Modal isOpen rounded>
      <div className="px-4 pt-4 pb-3 overflow-y-auto max-h-[60vh]">
        {state.title && (
          isInfo ? (
            <div className="flex items-center gap-1 mb-1.5">
              <InfoIcon />
              <p className="text-[16px] font-semibold uppercase leading-6 tracking-[0.32px] text-[#17181a]">
                {state.title}
              </p>
            </div>
          ) : (
            <p className="text-[16px] font-semibold uppercase leading-6 tracking-[0.32px] text-[#17181a] text-center mb-1.5">
              {state.title}
            </p>
          )
        )}
        <div
          className={`text-[15px] leading-5 text-[#5d5d5f] ${isInfo ? '' : 'text-center'}`}
          dangerouslySetInnerHTML={{ __html: state.message }}
        />
      </div>
      <ButtonDrawer buttons={displayButtons} modal />
    </Modal>,
    document.body
  )
}
